import { RouteName, routes } from '@shared/constants/routes'
import { getItem, setItem } from '@/features/shared/utils/localStorage'

type IsPageRoutes = Partial<Record<RouteName, boolean>>

// Shared dismissal key used by both banner and dialog variants.
export const APP_BANNER_DISMISSED_STORAGE_KEY = 'seen_continue_in_app_ui'

export const isAppBannerDismissed = () => {
  return getItem(APP_BANNER_DISMISSED_STORAGE_KEY) === 'true'
}

export const dismissAppBanner = () => {
  setItem(APP_BANNER_DISMISSED_STORAGE_KEY, 'true')
}

// Excluded routes for the skinny banner using base path matching (e.g., /shopper) to exclude
// entire route trees including all subroutes (e.g., /shopper/be-a-shopper, /shopper/shopper-safety).
const bannerExcludedPaths = [
  routes.CART.url,
  routes.CHECKOUT.url,
  routes.SHOPPER.url,
  routes.SUBSTITUTION_PREFERENCES.url,
  routes.NEED_ANYTHING_ELSE.url,
  routes.CART_BUILDER.url,
]

// Routes where the skinny banner shows. Uses RouteName enums (via useIsPage()) for
// pages with dynamic segments that are hard to match by path.
const skinnyBannerRoutes: RouteName[] = [
  RouteName.GLOBAL_HOMEPAGE,
  RouteName.RETAILER_HOMEPAGE,
  RouteName.SEARCH,
  RouteName.PRODUCT,
  RouteName.UNIVERSAL_PRODUCT,
]

// Routes where the drawer banner shows. Also uses RouteName enums for reliable
// matching on pages with dynamic URL structures.
const drawerBannerRoutes: RouteName[] = [
  RouteName.UNGATED_HOMEPAGE,
  RouteName.CITIES,
  RouteName.GLOBAL_SEARCH,
  RouteName.GLOBAL_LANDING_PAGE,
  RouteName.LANDING_PAGE,
  RouteName.RETAILER_LANDING_PAGE,
  RouteName.LANDING_PAGE_FOR_YOU,
]

const matchesAnyRoute = (isPage: IsPageRoutes, routeNames: readonly RouteName[]) => {
  return routeNames.some((routeName) => isPage[routeName])
}

const isCitiesPath = (pathname: string) => {
  return pathname === '/cities' || pathname.startsWith('/cities/')
}

const isExcludedBannerPath = (pathname: string) => {
  return bannerExcludedPaths.some((path) => pathname.startsWith(path))
}

export const getOpenTheAppBannerVariant = (
  isPage: IsPageRoutes,
  pathname: string,
  variant: 'banner' | 'drawer',
): boolean => {
  if (variant === 'banner') {
    if (!matchesAnyRoute(isPage, skinnyBannerRoutes)) {
      return false
    }
    return !isExcludedBannerPath(pathname)
  }

  // Drawer uses pure allowlist - no path exclusions needed
  return matchesAnyRoute(isPage, drawerBannerRoutes) || isCitiesPath(pathname)
}
